<script>
import EventSelectionRow from './EventSelectionRow.vue';
import FormGuideSortSelect from '../FormGuideSortSelect/FormGuideSortSelect.vue';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator.vue';
import { mapGetters } from 'vuex';
import config from './config';

export default {
  name: 'FormGuideEventCard',
  components: {
    EventSelectionRow,
    FormGuideSortSelect,
    LoadingIndicator,
  },
  props: {
    raceType: {
      type: String,
      required: true,
    },
    meetingName: {
      type: String,
      default: '',
    },
    eventId: {
      type: [String, Number],
      default: null,
    },
    slug: {
      type: String,
      default: null,
    },
    eventNumber: {
      type: String,
      default: null,
    },
    pageType: {
      type: String,
      default: 'form-guide',
    },
    bookmaker: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      filters: {
        showBets: false,
        showFlucs: false,
        showPlaceOdds: false,
        expandAll: false,
      },
      viewportWidth: window.innerWidth,
      // sorting options
      optionsGroup: config.sortingOptionsGroup,
    };
  },
  computed: {
    ...mapGetters({
      eventData: 'FormGuideEventCard/getEventData',
      selections: 'FormGuideEventCard/getSelections',
      liveBets: 'FormGuideEventCard/getLiveBets',
      getBestWinOdds: 'FormGuideEventCard/getSelectionBestWinOdds',
      getBestPlaceOdds: 'FormGuideEventCard/getSelectionBestPlaceOdds',
    }),
    eventPlaceWinners() {
      const { eventData } = this;
      return eventData.place_winners || config.defaultPlaceWinners;
    },
    results() {
      const { eventData, eventPlaceWinners } = this;
      if (eventData && eventData.is_resulted && eventData.results) {
        // order results
        const orderedResults = eventData.results.sort(
          (a, b) => parseInt(a.finish_position) - parseInt(b.finish_position)
        );
        // pass down first 3 places to selection row
        return orderedResults.slice(0, eventPlaceWinners);
      } else {
        return [];
      }
    },
    orderedSelections() {
      const { selections } = this;
      const orderedList = selections.sort((a, b) => parseInt(a.competitor_number) - parseInt(b.competitor_number));
      return orderedList;
    },
    isHarness() {
      return this.raceType === 'harness';
    },
    isMobile() {
      return this.viewportWidth < 768;
    },
    isAbandoned() {
      return this.eventData.status == 'Abandoned';
    },
    isFormGuidePage() {
      const { pageType } = this;
      return pageType === 'form-guide';
    },
    headers() {
      const { isFormGuidePage } = this;
      return !isFormGuidePage ? config.defaultRaceEventHeaders : config.defaultFormGuideHeaders;
    },
  },
  mounted() {
    const { eventId, setWindowWidth, setMeetingName, slug, eventNumber, raceType } = this;
    // add listener to handle page resize
    window.addEventListener('resize', setWindowWidth);
    setWindowWidth();
    // set event name
    setMeetingName();
    // fetch harness event data
    if (eventId) {
      this.$store.dispatch('FormGuideEventCard/fetchEventById', { eventId, raceType });
    } else if (slug && eventNumber) {
      this.$store.dispatch('FormGuideEventCard/fetchEventBySlug', { slug, eventNumber, raceType });
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setWindowWidth);
  },
  methods: {
    setRaceType() {
      this.$store.dispatch('FormGuideEventCard/setRaceType', this.raceType);
    },
    setMeetingName() {
      this.$store.dispatch('FormGuideEventCard/setMeetingName', this.meetingName);
    },
    toggleFilter(filterType) {
      if (filterType == 'showBets') {
        this.filters.showBets = !this.filters.showBets;
      }
      if (filterType == 'showFlucs') {
        this.filters.showFlucs = !this.filters.showFlucs;
      }
      if (filterType == 'showPlaceOdds') {
        this.filters.showPlaceOdds = !this.filters.showPlaceOdds;
      }
      if (filterType == 'expandAll') {
        this.filters.expandAll = !this.filters.expandAll;
      }
    },
    setWindowWidth() {
      this.viewportWidth = window.innerWidth;
    },
    getSelectionLiveBets(selectionId) {
      return this.liveBets.filter(bet => bet.selectionId === selectionId);
    },
    updateSort(value) {
      // TODO: Figure out how to sort selections
      console.log(value);
    },
    getSelectionBestOdds(selection) {
      const { showPlaceOdds } = this.filters;
      const bestWinOdds = selection.bestWinOdds || [];
      const bestPlaceOdds = selection.bestPlaceOdds || [];
      const winOdds = bestWinOdds && bestWinOdds.length > 0 ? bestWinOdds[0] : {};
      const placeOdds = bestPlaceOdds && bestPlaceOdds.length > 0 ? bestPlaceOdds[0] : {};
      return showPlaceOdds ? placeOdds : winOdds;
    },
    getSelectionFlucs(selection) {
      const { showPlaceOdds } = this.filters;
      const { averageWinOdds, averagePlaceOdds } = selection;
      const winFlucs = averageWinOdds && averageWinOdds.length > 0 ? averageWinOdds[0].price.fluctuations : [];
      const placeFlucs = averagePlaceOdds && averagePlaceOdds.length > 0 ? averagePlaceOdds[0].price.fluctuations : [];
      return showPlaceOdds ? placeFlucs : winFlucs;
    },
  },
};
</script>

<template>
  <div class="fg-event-card-container">
    <!-- desktop header -->
    <template v-if="!isMobile">
      <div class="fg-event-card__header-filters">
        <!-- Sort dropdown -->
        <div class="fg-event-card-filter-left">
          <FormGuideSortSelect
            v-if="!isHarness && isFormGuidePage"
            :options-group="optionsGroup"
            @input="updateSort"
          >
            <slot>Sort by</slot>
          </FormGuideSortSelect>
        </div>
        <!-- Filter buttons -->
        <template v-if="isFormGuidePage">
          <div class="fg-event-card-filter-right">
            <div class="fg-event-card-filter-buttons">
              <a
                v-if="!isHarness"
                :class="[{ active: filters.showBets }]"
                class="fg-event-card-filter-btn"
                href="#"
                @click.prevent="toggleFilter('showBets')"
              >
                <p>Live blog bets</p>
              </a>
              <a
                :class="[{ active: filters.showFlucs }]"
                class="fg-event-card-filter-btn"
                href="#"
                @click.prevent="toggleFilter('showFlucs')"
              >
                <p>Show flucs</p>
              </a>
              <a
                :class="[{ active: filters.showPlaceOdds }]"
                class="fg-event-card-filter-btn"
                href="#"
                @click.prevent="toggleFilter('showPlaceOdds')"
              >
                <p>Place Odds</p>
              </a>
              <a
                :class="[{ active: filters.expandAll }]"
                class="fg-event-card-filter-btn"
                href="#"
                @click.prevent="toggleFilter('expandAll')"
              >
                <p>Expand All</p>
              </a>
            </div>
          </div>
        </template>
      </div>

      <!-- column headers -->
      <div class="fg-event-card-header">
        <div class="fg-event-card-header-left">
          <div>Horse/Barrier</div>
        </div>
        <div class="fg-event-card-header-right">
          <template v-for="(header, index) in headers">
            <div
              :key="`header-${index}`"
              :class="{ 'full-width': !isFormGuidePage }"
              class="fg-event-card-header-right__header"
            >{{ header.title }}</div>
          </template>
          <template v-if="isFormGuidePage">
            <div
              :key="`header-flucs`"
              :class="{ 'full-width': !isFormGuidePage }"
              class="fg-event-card-header-right__header"
            >Flucs</div>
            <div
              :key="`header-bestodds`"
              :class="{ 'full-width': !isFormGuidePage }"
              class="fg-event-card-header-right__header"
            >Best Odds</div>
          </template>
          <template v-else>
            <div
              :key="`header-bookie-odds`"
              :class="{ 'full-width': !isFormGuidePage }"
              class="fg-event-card-header-right__header left-align"
            >Odds</div>
            <div
              :key="`header-join`"
              :class="{ 'full-width': !isFormGuidePage }"
              class="fg-event-card-header-right__header left-align"
            >Bonus Offer</div>
          </template>
        </div>
      </div>
    </template>

    <!-- mobile header -->
    <template v-else>
      <div v-if="isFormGuidePage" class="fg-event-card-header-mobile">
        <div class="fg-event-card-sort-mobile">
          <div v-if="!isHarness" class="fg-event-card-sort__icon"></div>
        </div>
        <div class="fg-event-card-filters-mobile">
          <div class="fg-event-card-filter-buttons">
            <a
              v-if="!isHarness"
              :class="[{ active: filters.showBets }]"
              class="fg-event-card-filter-btn"
              href="#"
              @click.prevent="toggleFilter('showBets')"
            >
              <p>Live blog bets</p>
            </a>
            <a
              :class="[{ active: filters.showFlucs }]"
              class="fg-event-card-filter-btn"
              href="#"
              @click.prevent="toggleFilter('showFlucs')"
            >
              <p>Show flucs</p>
            </a>
          </div>
        </div>
      </div>
    </template>

    <template v-if="selections.length === 0">
      <LoadingIndicator />
    </template>

    <!-- event selection rows -->
    <template v-else>
      <EventSelectionRow
        v-for="(selectionData, index) in orderedSelections"
        :data-event-selection-row="selectionData.id"
        :key="`event-sr-${index}`"
        :headers="headers"
        :selection-data="selectionData"
        :odds="getSelectionBestOdds(selectionData)"
        :flucs="getSelectionFlucs(selectionData)"
        :filters="filters"
        :is-mobile="isMobile"
        :live-bets="getSelectionLiveBets(selectionData.id)"
        :results="results"
        :is-abandoned="isAbandoned"
        :is-form-guide-page="isFormGuidePage"
        :conversion-bookmaker="bookmaker"
      />
    </template>
  </div>
</template>
